import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { TabHeader } from '../components/TabHeader';
import { TabSearchStrip } from '../components/TabSearchStrip';
import { Footer } from '../components/Footer';
import {
  SERVICE_CARDS,
  FEATURE_MATRIX,
  buildMailto,
  ServiceCard,
  MatrixRow,
} from '../data/pricing-data';

/**
 * Pricing tab — PRD §8. Above the fold: tailored-pilot explainer.
 * Then two service cards (Cloud + On-premise), a ~20-row feature
 * matrix, and the 20-question FAQ from Appendix D. Per-tab search
 * strip at the bottom for questions the FAQ does not cover.
 */
export function PricingPage(): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col bg-sovereign-900">
      <TabHeader />

      <main className="flex-1">
        <section className="border-b border-sovereign-line bg-brand-purple-dark px-4 py-10 text-white md:px-8 md:py-14">
          <div className="mx-auto max-w-6xl">
            <nav aria-label="Breadcrumb" className="mb-3 text-xs text-white/70">
              <Link to="/" className="hover:text-white hover:underline">
                Home
              </Link>
              <span className="mx-2" aria-hidden>·</span>
              <span>Pricing</span>
            </nav>
            <h1 className="text-3xl font-bold md:text-4xl">Pricing</h1>
          </div>
        </section>

        <section aria-labelledby="services" className="px-4 py-10 md:px-8 md:py-14">
          <div className="mx-auto max-w-6xl">
            <h2 id="services" className="sr-only">
              Service offerings
            </h2>
            <div className="grid gap-6 md:grid-cols-2">
              {SERVICE_CARDS.map((c) => (
                <ServiceCardBlock key={c.id} card={c} />
              ))}
            </div>
            <p className="mt-6 text-center text-sm text-ink-mute">
              Prefer a form?{' '}
              <Link to="/pricing/contact" className="text-brand-purple underline hover:text-brand-purple-dark">
                Use the contact form
              </Link>
              {' '}— routes to the same sales inbox as the mailto CTAs.
            </p>
          </div>
        </section>

        <section
          aria-labelledby="matrix"
          className="border-t border-sovereign-line bg-sovereign-850 px-4 py-10 md:px-8 md:py-14"
        >
          <div className="mx-auto max-w-6xl">
            <h2 id="matrix" className="mb-2 text-xl font-bold text-ink md:text-2xl">
              Cloud vs On-premise — feature matrix
            </h2>
            <p className="mb-6 text-sm text-ink-mute">
              Everything the platform ships. Anything marked <em>opt-in</em> is a Machine Learning Layer
              stage or a Sovereign Proxy add-on you can enable per tenant.
            </p>
            <FeatureMatrix rows={FEATURE_MATRIX} />
            <p className="mt-6 text-center text-sm text-ink-mute">
              Twenty common questions answered on the{' '}
              <Link
                to="/faq"
                className="text-brand-purple underline hover:text-brand-purple-dark"
              >
                FAQ tab
              </Link>
              .
            </p>
          </div>
        </section>
      </main>

      <TabSearchStrip
        slug="pricing"
        topic="pricing, service tiers, or onboarding"
        chips={[
          'How long is a typical pilot?',
          'What does an on-premise deployment cost?',
          'Can I upgrade from a Cloud pilot to on-prem later?',
        ]}
      />
      <Footer />
    </div>
  );
}

function ServiceCardBlock({ card }: { card: ServiceCard }): JSX.Element {
  const mailto = buildMailto(card);
  const border = card.emphasise
    ? 'border-2 border-brand-purple'
    : 'border border-sovereign-line';
  return (
    <article
      className={`flex flex-col rounded-lg bg-white p-6 shadow-card ${border}`}
    >
      <header className="mb-4">
        {card.emphasise && (
          <div className="mb-2 inline-block rounded-full bg-brand-purple px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
            Recommended pilot path
          </div>
        )}
        <h3 className="mb-1 text-xl font-bold text-ink">{card.title}</h3>
        <p className="text-sm text-brand-purple">{card.tagline}</p>
      </header>

      <p className="mb-5 text-sm leading-relaxed text-ink-mute">{card.description}</p>

      <div className="mb-4">
        <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-ink">
          What is included
        </h4>
        <ul className="space-y-1.5 text-sm text-ink">
          {card.included.map((s) => (
            <li key={s} className="flex gap-2">
              <span className="mt-1 text-brand-purple" aria-hidden>✓</span>
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mb-4">
        <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-ink">
          What is not included (customer-side)
        </h4>
        <ul className="space-y-1.5 text-sm text-ink-mute">
          {card.notIncluded.map((s) => (
            <li key={s} className="flex gap-2">
              <span className="mt-1 text-ink-dim" aria-hidden>·</span>
              <span>{s}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-auto flex flex-col gap-2 pt-2">
        <a
          href={mailto}
          className="inline-flex items-center justify-center rounded-md bg-brand-purple px-4 py-2.5 text-sm font-bold text-white shadow-card hover:bg-brand-purple-dark"
        >
          Contact sales
        </a>
        <div className="text-center text-[11px] text-ink-dim">
          Opens your mail client, pre-filled to sales@dideck.com
        </div>
      </div>
    </article>
  );
}

function FeatureMatrix({ rows }: { rows: MatrixRow[] }): JSX.Element {
  return (
    <div className="overflow-x-auto rounded-md border border-sovereign-line bg-white shadow-card">
      <table className="w-full min-w-[560px] text-left text-sm">
        <thead>
          <tr className="border-b border-sovereign-line bg-sovereign-900 text-xs uppercase tracking-wide text-ink-mute">
            <th className="w-1/2 px-4 py-2.5">Feature</th>
            <th className="w-1/4 px-4 py-2.5">Cloud</th>
            <th className="w-1/4 px-4 py-2.5">On-premise</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <Fragment key={i}>
              {r.section && (
                <tr className="bg-sovereign-850">
                  <td colSpan={3} className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide text-brand-purple">
                    {r.section}
                  </td>
                </tr>
              )}
              <tr className="border-t border-sovereign-line">
                <td className="px-4 py-2 text-ink">{r.feature}</td>
                <td className="px-4 py-2 text-ink-mute">{renderCell(r.cloud)}</td>
                <td className="px-4 py-2 text-ink-mute">{renderCell(r.onPrem)}</td>
              </tr>
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderCell(v: string | boolean): JSX.Element {
  if (v === true) return <span className="font-bold text-brand-purple" aria-label="Included">✓</span>;
  if (v === false) return <span className="text-ink-dim" aria-label="Not available">—</span>;
  return <span>{v}</span>;
}

